import React, { useState } from 'react';
import { 
  Dumbbell, Utensils, Brain, Droplets, Award, MessageCircle, 
  Leaf, Watch, Users, Video, Share2, Camera, Target, TrendingUp, 
  Calendar, Sparkles, BarChart3, Zap, Heart, ChevronDown, ChevronUp,
  Shield, Smartphone, UserCheck, ArrowRight
} from 'lucide-react';

const steps = [
  {
    number: '01',
    title: 'Create Your Profile',
    desc: 'Tell us about your body, fitness goals, target muscle groups, and dietary preferences. Our 4-step wizard captures everything we need.',
    icon: UserCheck,
    color: 'from-[#FF6600] to-[#FF8533]'
  },
  {
    number: '02',
    title: 'Get Your AI Plan',
    desc: 'Our AI analyzes your profile and generates a personalized workout + meal plan tailored to your goals, experience level, and preferences.',
    icon: Sparkles,
    color: 'from-purple-500 to-purple-600'
  },
  {
    number: '03',
    title: 'Track Everything',
    desc: 'Log meals by scanning food with your camera, track workouts, monitor water intake, and watch your progress with detailed analytics.',
    icon: Target,
    color: 'from-blue-500 to-blue-600'
  },
  {
    number: '04',
    title: 'Achieve Your Goals',
    desc: 'Earn badges, hit milestones, get AI coaching nudges, and share your transformation with the community.',
    icon: Award,
    color: 'from-green-500 to-green-600'
  },
];

const categories = [
  {
    title: 'Workout & Exercise',
    icon: Dumbbell,
    color: 'text-red-500',
    bg: 'bg-red-50 dark:bg-red-900/20',
    features: [
      { icon: Dumbbell, name: 'Exercise Library', desc: 'Browse 300+ exercises with professional 3D anatomical video demonstrations showing exactly which muscles are targeted. Each exercise includes sets, reps, and form guidance.' },
      { icon: Sparkles, name: 'AI Plan Generator', desc: 'Our AI creates a complete weekly workout plan personalized to your fitness goal, target muscles, training experience, and schedule.' },
      { icon: Calendar, name: 'Session Logger', desc: 'Log every workout session with sets, reps, and weight used. Build a complete training history and track personal records.' },
      { icon: TrendingUp, name: 'Workout Tracker', desc: 'Visualize your training volume, workout frequency, and strength progression over weeks and months.' },
      { icon: Brain, name: 'AI Exercise Coach', desc: 'Your personal AI trainer provides exercise suggestions, form tips, and workout modifications based on your progress and limitations.' },
    ]
  },
  {
    title: 'Nutrition & Meals',
    icon: Utensils,
    color: 'text-green-500',
    bg: 'bg-green-50 dark:bg-green-900/20',
    features: [
      { icon: Camera, name: 'AI Meal Scanner', desc: 'Simply take a photo of your food. Our AI instantly identifies every item and calculates calories, protein, carbs, fats, and even micronutrients.' },
      { icon: Utensils, name: 'Meal History & Analytics', desc: 'View your complete nutrition log with daily, weekly, and monthly breakdowns. See trends and patterns in your eating habits.' },
      { icon: Zap, name: 'Micronutrient Tracking', desc: 'Go beyond macros — track vitamins (A, C, D, B12) and minerals (Iron, Calcium, Magnesium) to ensure complete nutrition.' },
      { icon: Target, name: 'Smart Calorie Targets', desc: 'Adaptive calorie goals that automatically adjust based on your activity level, weight trend, and progress toward your goal.' },
      { icon: MessageCircle, name: 'AI Nutritionist Chat', desc: 'Ask any nutrition question and get personalized answers instantly. Your AI nutritionist considers your profile, allergies, and goals.' },
      { icon: Heart, name: 'AI Meal Planner', desc: 'Generate complete weekly meal plans that match your calorie targets, dietary preferences (vegan, keto, etc.), and food allergies.' },
    ]
  },
  {
    title: 'Recipes & Diet',
    icon: Leaf,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    features: [
      { icon: Leaf, name: 'Diet Recipe Hub', desc: 'Explore hundreds of healthy recipes filtered by diet type: Organic, Vegan, Gluten-Free, Heart-Healthy, Keto, and more.' },
      { icon: Utensils, name: 'What To Eat Suggestions', desc: "Not sure what to eat? Get personalized meal suggestions based on your remaining macros and the time of day." },
      { icon: Camera, name: 'Barcode Scanner', desc: 'Scan any packaged food barcode to instantly log its nutritional information. Works with millions of products.' },
      { icon: Target, name: 'Restaurant Finder', desc: 'Find healthy restaurant options near you with nutritional info, so you can stay on track even when eating out.' },
    ]
  },
  {
    title: 'Progress & Goals',
    icon: TrendingUp,
    color: 'text-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    features: [
      { icon: TrendingUp, name: 'Weight Tracker', desc: 'Log daily weigh-ins with a visual trend line. Set goal weight and see your projected timeline to reach it.' },
      { icon: Droplets, name: 'Water Tracker', desc: 'Set daily hydration goals and log water intake with a single tap. Get reminders to stay hydrated throughout the day.' },
      { icon: Camera, name: 'Progress Photos', desc: 'Take and compare progress photos with AI analysis that detects body composition changes over time.' },
      { icon: Award, name: 'Achievements & Badges', desc: 'Earn badges for consistency — workout streaks, meal logging, hydration goals, weight milestones, and more.' },
    ]
  },
  {
    title: 'Community & More',
    icon: Users,
    color: 'text-purple-500',
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    features: [
      { icon: Share2, name: 'Share Progress', desc: 'Share your achievements, transformation photos, and workout stats with friends on social media.' },
      { icon: Users, name: 'Referral Program', desc: 'Invite friends and earn rewards. Both you and your friend get premium benefits when they sign up.' },
      { icon: Watch, name: 'Wearable Devices', desc: 'Connect Fitbit, Apple Watch, or Samsung Galaxy Watch to sync steps, heart rate, sleep, and workout data.' },
      { icon: Smartphone, name: 'Works Everywhere', desc: 'Use NutriFitness AI on any device — iPhone, Android, tablet, or desktop. Your data syncs everywhere automatically.' },
    ]
  },
];

export default function HowItWorks() {
  const [expandedCategory, setExpandedCategory] = useState(0);

  return (
    <div className="max-w-4xl mx-auto pb-12" data-testid="how-it-works-page">
      {/* Hero */}
      <div className="text-center mb-10 pt-2">
        <div className="inline-flex items-center gap-2 bg-orange-100 dark:bg-orange-900/30 text-[#FF6600] px-4 py-1.5 rounded-full text-xs font-bold mb-4">
          <Sparkles size={14} /> YOUR COMPLETE GUIDE
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-white mb-3">
          How NutriFitness AI Works
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base max-w-xl mx-auto">
          Your AI-powered companion for nutrition, fitness, and wellness — everything you need in one app
        </p>
      </div>

      {/* 4 Steps Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
        {steps.map((step, i) => {
          const Icon = step.icon;
          return (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 text-center relative" data-testid={`step-${i+1}`}>
              <div className={`w-12 h-12 bg-gradient-to-r ${step.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                <Icon size={22} className="text-white" />
              </div>
              <div className="text-[10px] font-bold text-[#FF6600] mb-1">STEP {step.number}</div>
              <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-1">{step.title}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{step.desc}</p>
              {i < 3 && (
                <ArrowRight size={16} className="text-gray-300 dark:text-gray-600 dark:text-gray-300 absolute -right-2.5 top-1/2 -translate-y-1/2 hidden sm:block" />
              )}
            </div>
          );
        })}
      </div>

      {/* Detailed Features by Category */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center">
          All Features Explained
        </h2>
      </div>

      <div className="space-y-3">
        {categories.map((cat, catIndex) => {
          const CatIcon = cat.icon;
          const isExpanded = expandedCategory === catIndex;
          return (
            <div key={catIndex} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden" data-testid={`category-${catIndex}`}>
              <button
                onClick={() => setExpandedCategory(isExpanded ? -1 : catIndex)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-750 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${cat.bg} rounded-xl flex items-center justify-center`}>
                    <CatIcon size={20} className={cat.color} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white text-sm">{cat.title}</h3>
                    <span className="text-xs text-gray-400">{cat.features.length} features</span>
                  </div>
                </div>
                {isExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-gray-700 pt-3">
                  {cat.features.map((feature, fIndex) => {
                    const FIcon = feature.icon;
                    return (
                      <div key={fIndex} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-testid={`feature-${catIndex}-${fIndex}`}>
                        <div className={`w-8 h-8 ${cat.bg} rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <FIcon size={14} className={cat.color} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-gray-900 dark:text-white">{feature.name}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-0.5">{feature.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom CTA */}
      <div className="mt-10 bg-gradient-to-r from-[#FF6600] to-[#FF8533] rounded-2xl p-6 text-white text-center">
        <h3 className="text-lg font-bold mb-2">Ready to Start?</h3>
        <p className="text-orange-100 text-sm mb-1">
          Your AI-powered fitness journey begins with your profile. Set your goals, get your plan, and start tracking today.
        </p>
      </div>
    </div>
  );
}
